import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import {
  createStrategy,
  deactivateStrategy,
  deleteStrategy,
  extractStrategyRows,
  getStrategies,
  normalizeStrategies,
  updateStrategy,
} from '../../api/strategyLibraryService';
import './StrategyLibraryAdmin.css';

const STRATEGY_TYPES = [
  { value: 'intraday', label: 'Intraday' },
  { value: 'swing', label: 'Swing' },
  { value: 'investment', label: 'Investment' },
  { value: 'btst', label: 'BTST' },
];

const EMPTY_FORM = {
  name: '',
  chartinkUrl: '',
  category: 'bullish',
  strategyTypes: [],
  icon: '🎯',
  description: '',
};

const ADD_STRATEGY_CONFIRMATION = 'ADD STRATEGY';
const DELETE_STRATEGY_CONFIRMATION = 'DELETE STRATEGY';

const getStrategyId = (strategy) => strategy._id || strategy.id || strategy.slug;
const getStrategyIdentifiers = (strategy) => [
  strategy._id,
  strategy.id,
  strategy.slug,
  strategy.key,
  strategy.type,
].filter(Boolean).filter((value, index, identifiers) => identifiers.indexOf(value) === index);
const isStrategyNotFoundError = (err) => {
  const message = err.response?.data?.message || err.response?.data?.error || err.message || '';
  return message.toLowerCase().includes('strategy not found');
};

export default function StrategyLibraryAdmin() {
  const [strategies, setStrategies] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const activeCount = useMemo(
    () => strategies.filter((strategy) => strategy.isActive !== false).length,
    [strategies]
  );

  const loadStrategies = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getStrategies();
      setStrategies(normalizeStrategies(extractStrategyRows(data)));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load strategies');
      setStrategies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStrategies();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (type) => {
    setForm((prev) => ({
      ...prev,
      strategyTypes: prev.strategyTypes.includes(type)
        ? prev.strategyTypes.filter((item) => item !== type)
        : [...prev.strategyTypes, type],
    }));
  };

  const handleEdit = (strategy) => {
    setEditingId(getStrategyId(strategy));
    setForm({
      name: strategy.name || '',
      chartinkUrl: strategy.chartinkUrl || '',
      category: strategy.category || 'bullish',
      strategyTypes: Array.isArray(strategy.strategyTypes) ? strategy.strategyTypes : [],
      icon: strategy.icon || '🎯',
      description: strategy.description || '',
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!form.name.trim() || !form.chartinkUrl.trim()) {
      setError('Strategy name and Chartink URL are required.');
      return;
    }

    if (form.strategyTypes.length === 0) {
      setError('Select at least one strategy type.');
      return;
    }

    let confirmationText;
    if (!editingId) {
      const confirmation = window.prompt('Type ADD STRATEGY to confirm new strategy creation.');
      confirmationText = confirmation?.trim();
      if (confirmationText !== ADD_STRATEGY_CONFIRMATION) return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        chartinkUrl: form.chartinkUrl.trim(),
        category: form.category,
        strategyTypes: form.strategyTypes,
        icon: form.icon.trim() || '🎯',
        description: form.description.trim(),
      };

      if (!editingId) {
        payload.confirmationText = confirmationText;
      }

      if (editingId) {
        await updateStrategy(editingId, payload);
        setMessage('Strategy updated.');
      } else {
        await createStrategy(payload);
        setMessage('Strategy added.');
      }

      resetForm();
      await loadStrategies();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (strategy) => {
    const id = getStrategyId(strategy);
    if (!id) return;
    if (!window.confirm(`Deactivate ${strategy.name}?`)) return;

    setError('');
    setMessage('');
    try {
      await deactivateStrategy(id, {
        name: strategy.name || '',
        chartinkUrl: strategy.chartinkUrl || '',
        category: strategy.category || 'bullish',
        strategyTypes: strategy.strategyTypes || [],
        icon: strategy.icon || '🎯',
        description: strategy.description || '',
      });
      setMessage('Strategy deactivated.');
      await loadStrategies();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Deactivate failed');
    }
  };

  const handleDelete = async (strategy) => {
    const identifiers = getStrategyIdentifiers(strategy);
    if (identifiers.length === 0) return;
    const confirmation = window.prompt(`Type DELETE STRATEGY to permanently delete ${strategy.name}.`);
    const confirmationText = confirmation?.trim();
    if (confirmationText !== DELETE_STRATEGY_CONFIRMATION) return;

    setError('');
    setMessage('');
    try {
      for (const [index, identifier] of identifiers.entries()) {
        try {
          await deleteStrategy(identifier, { confirmationText });
          break;
        } catch (err) {
          if (index === identifiers.length - 1 || !isStrategyNotFoundError(err)) {
            throw err;
          }
        }
      }
      setMessage('Strategy deleted.');
      await loadStrategies();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Delete failed');
    }
  };

  return (
    <AppLayout pageBadge="🎯 Strategy Library">
      <div className="sla-root">
        <div className="sla-header">
          <div>
            <div className="sla-title">Strategy Library</div>
            <div className="sla-subtitle">Admin-managed strategy metadata for fetch, upload, radar, and summaries.</div>
          </div>
          <div className="sla-stats">
            <span>{activeCount} active</span>
            <span>{strategies.length} total</span>
          </div>
        </div>

        {(error || message) && (
          <div className={`sla-alert ${error ? 'error' : 'success'}`}>
            {error || message}
          </div>
        )}

        <div className="sla-grid">
          <form className="sla-form" onSubmit={handleSubmit}>
            <div className="sla-card-title">{editingId ? 'Edit Strategy' : 'Add Strategy'}</div>

            <label>
              <span>Name</span>
              <input value={form.name} onChange={(event) => handleChange('name', event.target.value)} placeholder="Parabolic" />
            </label>

            <label>
              <span>Chartink URL</span>
              <input value={form.chartinkUrl} onChange={(event) => handleChange('chartinkUrl', event.target.value)} placeholder="https://chartink.com/screener/..." />
            </label>

            <div className="sla-row">
              <label>
                <span>Category</span>
                <select value={form.category} onChange={(event) => handleChange('category', event.target.value)}>
                  <option value="bullish">Bullish</option>
                  <option value="bearish">Bearish</option>
                  <option value="neutral">Neutral</option>
                </select>
              </label>
              <label>
                <span>Icon</span>
                <input value={form.icon} onChange={(event) => handleChange('icon', event.target.value)} maxLength={4} />
              </label>
            </div>

            <fieldset className="sla-type-fieldset">
              <legend>Strategy Type</legend>
              <div className="sla-type-options">
                {STRATEGY_TYPES.map(({ value, label }) => (
                  <label key={value} className="sla-type-option">
                    <input
                      type="checkbox"
                      checked={form.strategyTypes.includes(value)}
                      onChange={() => handleTypeChange(value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label>
              <span>Description</span>
              <textarea value={form.description} onChange={(event) => handleChange('description', event.target.value)} rows={4} />
            </label>

            <div className="sla-form-actions">
              {editingId && <button type="button" className="sla-btn ghost" onClick={resetForm}>Cancel</button>}
              <button type="submit" className="sla-btn primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Strategy'}</button>
            </div>
          </form>

          <div className="sla-list-card">
            <div className="sla-list-head">
              <div className="sla-card-title">Strategies</div>
              <button className="sla-btn ghost" onClick={loadStrategies} disabled={loading}>Refresh</button>
            </div>

            {loading ? (
              <div className="sla-empty">Loading strategies...</div>
            ) : strategies.length === 0 ? (
              <div className="sla-empty">No strategy definitions found.</div>
            ) : (
              <div className="sla-table-wrap">
                <table className="sla-table">
                  <thead>
                    <tr>
                      <th>Strategy</th>
                      <th>Slug</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {strategies.map((strategy) => (
                      <tr key={getStrategyId(strategy)}>
                        <td>
                          <span className="sla-icon">{strategy.icon}</span>
                          <span>{strategy.name}</span>
                        </td>
                        <td>{strategy.slug || '—'}</td>
                        <td><span className={`sla-category ${strategy.category}`}>{strategy.category}</span></td>
                        <td>
                          <div className="sla-type-tags">
                            {(strategy.strategyTypes || []).length > 0
                              ? strategy.strategyTypes.map((type) => <span key={type}>{type}</span>)
                              : <span className="sla-unassigned">Unassigned</span>}
                          </div>
                        </td>
                        <td>{strategy.isActive === false ? <span className="sla-status off">Inactive</span> : <span className="sla-status on">Active</span>}</td>
                        <td>
                          <div className="sla-actions">
                            <button className="sla-btn mini" onClick={() => handleEdit(strategy)}>Edit</button>
                            {strategy.isActive !== false && <button className="sla-btn mini warn" onClick={() => handleDeactivate(strategy)}>Deactivate</button>}
                            <button className="sla-btn mini danger" onClick={() => handleDelete(strategy)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}